// ---------- l'onglet « Ensemble » : la semaine de tous les joueurs ----------
// Le second onglet du trophée (src/progression.ts), à la place de la ligue. Le pourquoi du
// remplacement est dans backend/src/utils/community.ts ; ce qui compte ici : AUCUN NOM, AUCUN
// RANG. L'écran dit qu'on n'est pas seul à jouer (le total de la semaine, jour par jour), ce qui a
// plu (le dessin le plus peint, l'alphabet du moment), et la seule ligne sur le joueur est une
// PART — « 5 puzzles terminés sur 2 340 » — jamais une place.
//
// Comme « Moi », la dernière réponse est gardée sur l'appareil et peinte tout de suite : l'onglet
// s'ouvre plein, même hors ligne, et la réponse fraîche le remplace.

import { escapeHtml } from './html';
import { currentUser } from './account';
import { fetchCommunity, type CommunityReport } from './api';
import { currentLang, t, WEEK_START, weekdayInitials, type I18nKey } from './i18n';
import { fingerprint } from './badgeQueue';
import { progressIndex } from './progress';
import { openPack, openSheet, sheetInCatalogue } from './sheetLinks';
import { techniqueIconLg } from './techniqueIcon';

const CACHE_KEY = 'codigrid:community:v1';
const DAY = 24 * 60 * 60 * 1000;

let current: CommunityReport | null = null;
/** L'empreinte du compte à qui appartient `current` : la ligne « ta part » est personnelle, et un
 *  code de reprise échangé hors ligne montrait celle de l'ANCIEN compte. */
let currentOwner: string | null = null;
/** Quand `current` est arrivé du serveur. L'instantané du serveur ne bouge qu'une fois par heure :
 *  repasser de « Moi » à « Ensemble » ne redemande rien pendant cinq minutes, et deux passages
 *  rapprochés partagent la même requête. */
let freshAt = 0;
let inflight: Promise<void> | null = null;
const FRESH_MS = 5 * 60 * 1000;
let visible = false;
/** Ce que fait « Le peindre » — c'est progression.ts qui sait fermer l'écran et ouvrir le
 *  calendrier ; ce module ne connaît ni l'un ni l'autre (même sens de dépendance que
 *  `wireDaily(opener)`). */
let goDaily: () => void = () => {};

/** Relu pour le seul compte qui l'a reçu (`owner`) ; un cache sans empreinte est ignoré. */
function readCache(owner: string): CommunityReport | null {
  try {
    const raw = JSON.parse(localStorage.getItem(CACHE_KEY) ?? 'null') as
      (CommunityReport & { owner?: string }) | null;
    return raw && raw.owner === owner ? raw : null;
  } catch {
    return null;
  }
}

function writeCache(r: CommunityReport, owner: string): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...r, owner }));
  } catch {
    /* pas de stockage : l'onglet attendra le réseau à chaque ouverture, rien de plus */
  }
}

/** Les nombres à la façon de la langue : « 2 340 » en français, « 2,340 » en anglais. */
function num(n: number): string {
  return new Intl.NumberFormat(currentLang()).format(n);
}

function plural(base: string, n: number, vars: Record<string, string | number> = {}): string {
  return t(`${base}.${n === 1 ? 'one' : 'other'}` as I18nKey, { n: num(n), ...vars });
}

const esc = escapeHtml;

/**
 * L'histogramme de la semaine. Les compteurs arrivent LUNDI D'ABORD, parce que la semaine du
 * serveur est la même pour tout le monde (lundi 00:00 UTC) ; l'anglais du jeu, lui, commence ses
 * semaines un dimanche (WEEK_START). Les initiales sont donc remises dans l'ordre des barres, et
 * non l'inverse : déplacer les barres ferait commencer le graphique au milieu de la semaine qu'il
 * compte.
 */
function bars(r: CommunityReport): string {
  const local = weekdayInitials();
  const initials = WEEK_START[currentLang()] === 7 ? [...local.slice(1), local[0]] : local;
  const today = Math.floor((Date.parse(`${r.day}T00:00:00Z`) - Date.parse(r.weekStart)) / DAY);
  const max = Math.max(1, ...r.perDay);
  const cols = r.perDay
    .map((n, i) => {
      const state = i < today ? 'past' : i === today ? 'today' : 'future';
      // 6 % au moins : une journée à zéro reste une barre visible, pas un trou dans la rangée.
      const h = state === 'future' ? 6 : Math.max(6, Math.round((n / max) * 100));
      return `<i class="${state}" style="height:${h}%"></i>`;
    })
    .join('');
  const days = initials
    .map((d, i) => `<span${i === today ? ' class="today"' : ''}>${esc(d)}</span>`)
    .join('');
  return `<div class="tg-bars" aria-hidden="true">${cols}</div><div class="tg-days" aria-hidden="true">${days}</div>`;
}

function week(r: CommunityReport): string {
  if (!r.sheets) {
    return `
      <section class="tg-week">
        <div class="tg-eyebrow">${t('together.eyebrow')}</div>
        <p class="tg-empty">${t('together.empty')}</p>
      </section>`;
  }
  return `
    <section class="tg-week">
      <div class="tg-eyebrow">${t('together.eyebrow')}</div>
      <div class="tg-big"><span class="tg-n">${num(r.sheets)}</span><span>${t(
        r.sheets === 1 ? 'together.sheets.one' : 'together.sheets.other',
      )}</span></div>
      <div class="tg-sub">${plural('together.players', r.players)}</div>
      ${bars(r)}
    </section>`;
}

function mine(r: CommunityReport): string {
  const line =
    r.mine === 0
      ? t('together.mine.zero')
      : plural('together.mine', r.mine, { total: num(Math.max(r.sheets, r.mine)) });
  return `
    <div class="tg-mine">
      <span class="tg-mine-n">${num(r.mine)}</span>
      <span class="tg-mine-t"><b>${t('together.mine.title')}</b><br>${line}</span>
    </div>`;
}

// ---------- la semaine en chiffres ----------
// À la place de « Ce qui a plu » (le dessin le plus peint, l'alphabet du moment), remplacé le
// 2026-10-10 : un titre souvent caché (« Un dessin à découvrir ») et un nom d'alphabet ne disaient
// pas grand-chose. Quatre chiffres disent mieux que la communauté est vivante. Chacun se tait
// quand il n'a rien de juste à dire, et une réponse en cache d'avant ces champs n'en montre aucun.

/** En dessous, une comparaison ou une heure de pointe ne veut rien dire : trois feuilles de plus
 *  feraient « +300 % », et une seule partie nocturne ferait de 3 h l'heure de la communauté. */
const MIN_SAMPLE = 20;

function stat(eyebrow: string, big: string, what: string, extra = '', bigClass = ''): string {
  return `
    <div class="tg-card tg-stat">
      <span class="tg-eyebrow">${eyebrow}</span>
      <span class="tg-stat-n${bigClass}">${big}</span>
      <span class="tg-name">${what}</span>
      ${extra}
    </div>`;
}

/** La tendance, contre la semaine dernière AU MÊME MOMENT (cf. lastWeekSoFar côté serveur). Une
 *  semaine plus calme ne s'affiche pas en pourcentage rouge : « un peu plus calme » suffit, et
 *  rien ne sert de souligner un creux à qui vient jouer pour se détendre. */
function trend(r: CommunityReport): string {
  const last = r.lastWeekSoFar;
  if (last === undefined || last < MIN_SAMPLE || r.sheets < MIN_SAMPLE) return '';
  const pct = Math.round(((r.sheets - last) / last) * 100);
  const ctx = `<span class="tg-count">${t('together.trend.ctx', { n: num(r.sheets), last: num(last) })}</span>`;
  if (pct >= 5)
    return stat(t('together.trend.eyebrow'), `+${num(pct)} %`, t('together.trend.up'), ctx, ' up');
  return stat(
    t('together.trend.eyebrow'),
    num(r.sheets),
    t(pct > -5 ? 'together.trend.same' : 'together.trend.calm'),
    ctx,
  );
}

/**
 * L'heure où l'on colorie le plus, À L'HEURE DU JOUEUR. Le serveur compte par heure UTC (il n'a
 * pas d'heure locale) ; on fait tourner les 24 cases du décalage de l'appareil — celui
 * d'aujourd'hui, ce qui suffit à une semaine. Un fuseau à la demi-heure (l'Inde) est arrondi :
 * la case dit « vers 21 h », pas « à 21 h 30 ».
 */
function peak(r: CommunityReport): string {
  const hours = r.perHour;
  if (!hours || hours.length !== 24 || r.sheets < MIN_SAMPLE) return '';
  const shift = Math.round(-new Date().getTimezoneOffset() / 60);
  const local = hours.map((_, h) => hours[(((h - shift) % 24) + 24) % 24]);
  const max = Math.max(...local);
  const at = local.indexOf(max);
  const label = new Intl.DateTimeFormat(currentLang(), { hour: 'numeric' }).format(
    new Date(2026, 0, 1, at),
  );
  const cols = local
    .map(
      (n, h) =>
        `<i${h === at ? ' class="peak"' : ''} style="height:${Math.max(8, Math.round((n / max) * 100))}%"></i>`,
    )
    .join('');
  return stat(
    t('together.peak.eyebrow'),
    esc(label),
    t('together.peak.what'),
    `<span class="tg-hours" aria-hidden="true">${cols}</span>`,
  );
}

const METALS = ['bronze', 'argent', 'or', 'platine', 'prisme'] as const;

/** Les paliers franchis depuis lundi, avec les pastilles de leurs métaux — mêmes couleurs que le
 *  décompte de l'écran « Moi » (.me-metal). */
function crossed(r: CommunityReport): string {
  const by = r.crossed;
  if (!by) return '';
  const total = by.reduce((a, b) => a + b, 0);
  if (!total) return '';
  const metals = by
    .map((n, i) =>
      n ? `<span class="me-metal"><i class="m-${METALS[i]}"></i>${num(n)}</span>` : '',
    )
    .join('');
  return stat(
    t('together.crossed.eyebrow'),
    num(total),
    t(`together.crossed.${total === 1 ? 'one' : 'other'}` as I18nKey),
    `<span class="me-metals tg-metals">${metals}</span>`,
  );
}

function joined(r: CommunityReport): string {
  const n = r.newPlayers;
  if (!n) return '';
  const last = r.newPlayersLastWeekSoFar;
  const ctx = last
    ? `<span class="tg-count">${t('together.joined.ctx', { last: num(last) })}</span>`
    : '';
  return stat(
    t('together.joined.eyebrow'),
    num(n),
    t(`together.joined.${n === 1 ? 'one' : 'other'}` as I18nKey),
    ctx,
  );
}

/** Le puzzle du jour : combien l'ont peint, et une porte pour y aller — sauf pour qui l'a déjà
 *  fini, à qui on ne propose pas de refaire ce qu'il vient de faire. Le chiffre se dit « avec
 *  toi » chez lui, comme sur la carte de fin. Rien du tout tant que personne n'a fini le jour :
 *  « 0 joueur » ne ferait que souligner un vide (même règle que le calendrier, cf. daily.ts). */
function daily(r: CommunityReport): string {
  // `dailyOthers` exclut toujours le joueur (cf. getCommunity) : il s'y ajoute ici s'il a fini.
  // Absent d'une réponse en cache d'avant ce champ : la ligne attend la réponse fraîche.
  const others = r.dailyOthers ?? 0;
  const done = !!(r.dailyPuzzleId && progressIndex().get(r.dailyPuzzleId)?.done);
  // Personne d'autre : « 0 joueur » soulignerait le vide, et « avec toi, 1 joueur » ne dirait rien.
  if (!others) return '';
  const text = done
    ? t('together.daily.with', { n: num(others + 1) })
    : plural('together.daily', others);
  return `
    <div class="tg-daily">
      <svg class="i" aria-hidden="true"><use href="#i-daily"></use></svg>
      <span>${text}</span>
      ${done ? '' : `<button class="btn primary" id="tgDailyGo">${t('together.daily.go')}</button>`}
    </div>`;
}

/** Un grand nombre lu d'un coup d'œil : « 1,2 M » plutôt que « 1 234 567 ». Exact en dessous de
 *  cent mille, où chaque chiffre se lit encore. */
function compact(n: number): string {
  if (n < 100_000) return num(n);
  return new Intl.NumberFormat(currentLang(), {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(n);
}

/** Les cases peintes par tout le monde depuis lundi : le chiffre collectif le plus parlant — un
 *  puzzle est un nombre, une case est un geste. Calculé dans l'instantané horaire, absent d'un
 *  instantané d'avant ce champ. */
function cells(r: CommunityReport): string {
  if (!r.cells || r.sheets < MIN_SAMPLE) return '';
  return stat(t('together.cells.eyebrow'), compact(r.cells), t('together.cells.what'));
}

/** La meilleure journée de la semaine, tirée de l'histogramme déjà reçu : aucun calcul serveur.
 *  Seulement une fois deux jours passés — un lundi, « meilleure journée : lundi » ne dit rien. */
function best(r: CommunityReport): string {
  const today = Math.floor((Date.parse(`${r.day}T00:00:00Z`) - Date.parse(r.weekStart)) / DAY);
  if (today < 1 || r.sheets < MIN_SAMPLE) return '';
  let i = 0;
  for (let k = 1; k <= Math.min(today, 6); k++) if (r.perDay[k] > r.perDay[i]) i = k;
  const n = r.perDay[i];
  if (!n) return '';
  // Le jour en UTC, comme le serveur l'a compté : sa journée va de minuit à minuit UTC.
  const name = new Intl.DateTimeFormat(currentLang(), { weekday: 'long', timeZone: 'UTC' }).format(
    Date.parse(r.weekStart) + i * DAY,
  );
  return stat(
    t('together.best.eyebrow'),
    name.charAt(0).toUpperCase() + name.slice(1),
    plural('together.best', n),
    '',
    ' word',
  );
}

/**
 * Le puzzle le mieux noté de la semaine. SANS SON IMAGE NI SON TITRE tant que le joueur ne l'a pas
 * peint : c'est la règle du catalogue (cf. buildCard dans home.ts), on découvre le sujet en
 * coloriant. On montre la technique, la note et le nombre de votes ; le titre et la vignette
 * reviennent une fois la feuille finie. « Le peindre » seulement si la feuille est dans le
 * catalogue de ce joueur — dans un pack pas débloqué, l'ouvrir finirait sur « indisponible ».
 */
function topRated(r: CommunityReport): string {
  const top = r.topRated;
  if (!top) return '';
  const mine = progressIndex().get(top.puzzleId);
  const done = !!mine?.done;
  const art =
    done && mine?.thumb
      ? `<img class="tg-thumb" src="${esc(mine.thumb)}" alt="" />`
      : `<span class="tg-tech">${techniqueIconLg(top.alphabetId)}</span>`;
  // Pas dans le catalogue du joueur : la feuille est dans un pack qu'il n'a pas débloqué. On le lui
  // propose — c'est une bonne raison de débloquer, et la seule façon d'y arriver.
  const action = done
    ? `<span class="tg-count">${t('together.top.done')}</span>`
    : sheetInCatalogue(top.puzzleId)
      ? `<button class="btn primary" id="tgTopGo">${t('together.daily.go')}</button>`
      : top.packId
        ? `<button class="btn primary" id="tgTopPack">${t('together.top.unlock')}</button>`
        : `<span class="tg-count">${t('together.top.locked')}</span>`;
  return `
    <div class="tg-card tg-top">
      ${art}
      <span class="tg-top-t">
        <span class="tg-eyebrow">${t('together.top.eyebrow')}</span>
        ${done && top.title.trim() ? `<span class="tg-name">${esc(top.title)}</span>` : ''}
        <span class="tg-stars"><svg class="i" aria-hidden="true"><use href="#i-star"></use></svg>${num(top.avg)} · ${plural('together.top.votes', top.votes)}</span>
      </span>
      ${action}
    </div>`;
}

function paint(host: HTMLElement): void {
  if (!visible) return;
  const r = current;
  if (!r) {
    host.innerHTML = `<p class="prog-empty">${t('progression.loading')}</p>`;
    return;
  }
  const cards = trend(r) + peak(r) + cells(r) + best(r) + crossed(r) + joined(r);
  host.innerHTML = `
    <div class="together">
      ${week(r)}
      ${mine(r)}
      ${cards ? `<div class="me-sec-h"><h2>${t('together.stats')}</h2></div><div class="tg-pair">${cards}</div>` : ''}
      ${topRated(r)}
      ${daily(r)}
    </div>`;
  const go = document.getElementById('tgDailyGo');
  if (go) go.onclick = () => goDaily();
  const top = document.getElementById('tgTopGo');
  const topId = r.topRated?.puzzleId;
  if (top && topId) top.onclick = () => openSheet(topId);
  const pack = document.getElementById('tgTopPack');
  const packId = r.topRated?.packId;
  if (pack && packId) pack.onclick = () => openPack(packId);
}

/** Peint l'onglet tout de suite (cache), puis le rafraîchit depuis le serveur. */
export async function showTogether(host: HTMLElement): Promise<void> {
  visible = true;
  const token = await currentUser();
  if (!token) {
    // Pas encore de compte : il naît à la première sauvegarde. Les chiffres demandent le jeton
    // (la ligne « ta part » est personnelle), donc on dit simplement comment y arriver.
    // `visible` revérifié : le joueur a pu revenir sur « Moi » pendant l'attente du jeton.
    if (visible) host.innerHTML = `<p class="prog-empty">${t('progression.needAccount')}</p>`;
    return;
  }
  const owner = fingerprint(token);
  if (currentOwner !== owner) {
    current = readCache(owner);
    currentOwner = owner;
    freshAt = 0;
  }
  paint(host);
  if (current && Date.now() - freshAt < FRESH_MS) return;
  inflight ??= (async () => {
    try {
      const fresh = await fetchCommunity(token);
      // Le compte a changé pendant la requête : c'est la part de l'ancien.
      if ((await currentUser()) !== token) return;
      current = fresh;
      currentOwner = owner;
      freshAt = Date.now();
      writeCache(fresh, owner);
      paint(host);
    } catch {
      if (visible && !current)
        host.innerHTML = `<p class="prog-empty">${t('progression.loadFailed')}</p>`;
    } finally {
      inflight = null;
    }
  })();
  await inflight;
}

export function hideTogether(): void {
  visible = false;
}

export function wireTogether(onGoDaily: () => void): void {
  goDaily = onGoDaily;
}
